export const EMPLOYEE_PERSONAL_SECTIONS = [
  'identity',
  'employment',
  'contacts',
  'documents',
  'addresses',
  'family',
] as const;

export type EmployeePersonalSection = (typeof EMPLOYEE_PERSONAL_SECTIONS)[number];
export type EmployeePersonalSectionDecision = 'UNCHANGED' | 'NEEDS_ATTENTION';

export const EMPLOYEE_PERSONAL_SECTION_LABELS: Record<EmployeePersonalSection, string> = {
  identity: 'ФИО и дата рождения',
  employment: 'Должность',
  contacts: 'Контакты',
  documents: 'Паспорт/ID',
  addresses: 'Адрес регистрации',
  family: 'Семья',
};

export const EMPLOYEE_PERSONAL_SECTION_CHECK_LABELS: Record<EmployeePersonalSection, string> = {
  identity: 'ФИО и дата рождения не менялись',
  employment: 'Должность не менялась',
  contacts: 'Личный телефон не менялся',
  documents: 'Паспорт/ID не менялся',
  addresses: 'Прописка не менялась',
  family: 'Семейное положение и дети не менялись',
};

export interface EmployeePersonalFieldSpec {
  section: EmployeePersonalSection;
  path: string;
  label: string;
  inputType?: 'text' | 'date' | 'textarea' | 'select' | 'emergencyContacts';
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  requiredIfPath?: string;
}

export interface EmployeePersonalEmergencyContact {
  name: string;
  phone: string;
  relation: string;
}

export const EMPLOYEE_PERSONAL_FORM_CASE_OPTIONS = [
  { key: 'nominative', label: 'Именительный' },
  { key: 'genitive', label: 'Родительный' },
  { key: 'dative', label: 'Дательный' },
  { key: 'accusative', label: 'Винительный' },
  { key: 'instrumental', label: 'Творительный' },
  { key: 'prepositional', label: 'Предложный' },
] as const;

export const EMPLOYEE_PERSONAL_FORM_CASE_ORDER = [
  'base',
  ...EMPLOYEE_PERSONAL_FORM_CASE_OPTIONS.map((item) => item.key),
] as const;

export type EmployeePersonalFormCase = (typeof EMPLOYEE_PERSONAL_FORM_CASE_ORDER)[number];
export type EmployeePersonalDocumentFormCase = (typeof EMPLOYEE_PERSONAL_FORM_CASE_OPTIONS)[number]['key'];

export const EMPLOYEE_PERSONAL_FORM_CASE_LABELS: Record<EmployeePersonalFormCase, string> = {
  base: 'Основное',
  nominative: 'Именительный',
  genitive: 'Родительный',
  dative: 'Дательный',
  accusative: 'Винительный',
  instrumental: 'Творительный',
  prepositional: 'Предложный',
};

export const EMPLOYEE_PERSONAL_NAME_PART_OPTIONS = [
  { key: 'lastName', label: 'Фамилия', fieldLabel: 'фамилия' },
  { key: 'firstName', label: 'Имя', fieldLabel: 'имя' },
  { key: 'middleName', label: 'Отчество', fieldLabel: 'отчество' },
] as const;

export const EMPLOYEE_PERSONAL_FIELD_SPECS: EmployeePersonalFieldSpec[] = [
  { section: 'identity', path: 'identity.lastName', label: 'Фамилия', required: true },
  { section: 'identity', path: 'identity.firstName', label: 'Имя', required: true },
  { section: 'identity', path: 'identity.middleName', label: 'Отчество' },
  { section: 'identity', path: 'identity.birthDate', label: 'Дата рождения', inputType: 'date', required: true },
  ...EMPLOYEE_PERSONAL_FORM_CASE_OPTIONS.flatMap((caseOption) => EMPLOYEE_PERSONAL_NAME_PART_OPTIONS.map((partOption) => ({
    section: 'identity' as const,
    path: `identity.nameForms.${caseOption.key}.${partOption.key}`,
    label: `ФИО: ${caseOption.label}, ${partOption.fieldLabel}`,
    required: true,
    ...(partOption.key === 'middleName' ? { requiredIfPath: 'identity.middleName' } : {}),
  }))),
  { section: 'employment', path: 'employment.position', label: 'Должность', required: true },
  ...EMPLOYEE_PERSONAL_FORM_CASE_OPTIONS.map((item) => ({
    section: 'employment' as const,
    path: `employment.positionForms.${item.key}`,
    label: `Должность: ${item.label}`,
    required: true,
  })),
  { section: 'contacts', path: 'contacts.personalPhone', label: 'Личный телефон', placeholder: '+375291234567' },
  {
    section: 'contacts',
    path: 'contacts.emergencyContacts',
    label: 'Контакты на крайний случай',
    inputType: 'emergencyContacts',
  },
  { section: 'documents', path: 'documents.personalNumber', label: 'Идентификационный номер' },
  {
    section: 'documents',
    path: 'documents.identityDocument.type',
    label: 'Тип документа',
    inputType: 'select',
    options: [
      { value: '', label: 'Не указан' },
      { value: 'PASSPORT', label: 'Паспорт' },
      { value: 'ID_CARD', label: 'ID-карта' },
      { value: 'OTHER', label: 'Другой документ' },
    ],
  },
  { section: 'documents', path: 'documents.identityDocument.series', label: 'Серия' },
  { section: 'documents', path: 'documents.identityDocument.number', label: 'Номер' },
  { section: 'documents', path: 'documents.identityDocument.issuedBy', label: 'Кем выдан', inputType: 'textarea' },
  { section: 'documents', path: 'documents.identityDocument.issuedAt', label: 'Дата выдачи', inputType: 'date' },
  { section: 'documents', path: 'documents.identityDocument.expiresAt', label: 'Действителен до', inputType: 'date' },
  { section: 'addresses', path: 'addresses.registration', label: 'Адрес регистрации', inputType: 'textarea' },
  {
    section: 'family',
    path: 'family.maritalStatus',
    label: 'Семейное положение',
    inputType: 'select',
    options: [
      { value: 'UNKNOWN', label: 'Не указано' },
      { value: 'SINGLE', label: 'Не состоит в браке' },
      { value: 'MARRIED', label: 'Состоит в браке' },
      { value: 'DIVORCED', label: 'Разведен(а)' },
      { value: 'WIDOWED', label: 'Вдовец/вдова' },
    ],
  },
  {
    section: 'family',
    path: 'family.children',
    label: 'Дети',
    inputType: 'textarea',
    placeholder: 'Только годы или даты рождения через запятую: 2012, 14.05.2015',
  },
];

export function employeePersonalFieldsForSection(section: EmployeePersonalSection): EmployeePersonalFieldSpec[] {
  return EMPLOYEE_PERSONAL_FIELD_SPECS.filter((field) => field.section === section);
}

export function employeePersonalFieldSpec(path: string): EmployeePersonalFieldSpec | undefined {
  return EMPLOYEE_PERSONAL_FIELD_SPECS.find((field) => field.path === path);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeEmergencyContact(value: unknown): EmployeePersonalEmergencyContact | null {
  if (!isRecord(value)) return null;
  return {
    name: typeof value.name === 'string' ? value.name : '',
    phone: typeof value.phone === 'string' ? value.phone : '',
    relation: typeof value.relation === 'string'
      ? value.relation
      : typeof value.relationship === 'string'
        ? value.relationship
        : '',
  };
}

function hasEmergencyContactValue(contact: EmployeePersonalEmergencyContact): boolean {
  return Boolean(contact.name.trim() || contact.phone.trim() || contact.relation.trim());
}

export function normalizeEmployeePersonalEmergencyContacts(
  value: unknown,
  options: { includeEmpty?: boolean } = {},
): EmployeePersonalEmergencyContact[] {
  if (!Array.isArray(value)) return [];
  const contacts = value
    .map((item) => normalizeEmergencyContact(item))
    .filter((item): item is EmployeePersonalEmergencyContact => Boolean(item));
  return options.includeEmpty ? contacts : contacts.filter(hasEmergencyContactValue);
}

export function formatEmployeePersonalEmergencyContacts(value: unknown): string {
  return normalizeEmployeePersonalEmergencyContacts(value)
    .map((contact) => {
      const name = contact.name.trim();
      const phone = contact.phone.trim();
      const relation = contact.relation.trim();
      const person = [name, relation ? `(${relation})` : ''].filter(Boolean).join(' ');
      return [person, phone].filter(Boolean).join(': ');
    })
    .filter(Boolean)
    .join('\n');
}

export function getEmployeePersonalEmergencyContacts(
  payload: Record<string, unknown>,
  options: { includeEmpty?: boolean } = {},
): EmployeePersonalEmergencyContact[] {
  const contacts = isRecord(payload.contacts)
    ? payload.contacts.emergencyContacts
    : undefined;
  return normalizeEmployeePersonalEmergencyContacts(contacts, options);
}

export function getEmployeePersonalValue(payload: Record<string, unknown>, path: string): string {
  const parts = path.split('.');
  let value: unknown = payload;
  for (const part of parts) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
    value = (value as Record<string, unknown>)[part];
  }
  if (value === null || value === undefined) return '';
  if (path === 'contacts.emergencyContacts') {
    return formatEmployeePersonalEmergencyContacts(value);
  }
  if (path === 'family.children') {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            const child = item as Record<string, unknown>;
            const birthValue = child.birthDate ?? child.dateOfBirth ?? child.dob ?? child.date ?? child.year;
            return birthValue == null ? '' : String(birthValue);
          }
          return String(item);
        })
        .map((item) => item.trim())
        .filter(Boolean)
        .join(', ');
    }
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return JSON.stringify(item);
        return String(item);
      })
      .join('\n');
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

export function getEmployeePersonalDisplayValue(payload: Record<string, unknown>, path: string): string {
  const value = getEmployeePersonalValue(payload, path);
  const spec = employeePersonalFieldSpec(path);
  if (!spec?.options) return value;
  return spec.options.find((option) => option.value === value)?.label ?? value;
}

export function isEmployeePersonalFieldRequired(
  payload: Record<string, unknown>,
  field: EmployeePersonalFieldSpec,
): boolean {
  if (!field.required) return false;
  if (!field.requiredIfPath) return true;
  return getEmployeePersonalValue(payload, field.requiredIfPath).trim().length > 0;
}

export function employeePersonalRequiredFieldsForSection(
  section: EmployeePersonalSection,
  payload: Record<string, unknown>,
): EmployeePersonalFieldSpec[] {
  return employeePersonalFieldsForSection(section)
    .filter((field) => isEmployeePersonalFieldRequired(payload, field));
}

export function employeePersonalMissingRequiredFields(
  payload: Record<string, unknown>,
  sections: readonly EmployeePersonalSection[] = EMPLOYEE_PERSONAL_SECTIONS,
): EmployeePersonalFieldSpec[] {
  const selectedSections = new Set<EmployeePersonalSection>(sections);
  return EMPLOYEE_PERSONAL_FIELD_SPECS
    .filter((field) => selectedSections.has(field.section))
    .filter((field) => isEmployeePersonalFieldRequired(payload, field))
    .filter((field) => getEmployeePersonalValue(payload, field.path).trim().length === 0);
}

export function employeePersonalSectionHasFieldChanges(
  currentPayload: Record<string, unknown>,
  baselinePayload: Record<string, unknown> | null | undefined,
  section: EmployeePersonalSection,
): boolean {
  if (!baselinePayload) return false;
  return employeePersonalFieldsForSection(section).some(
    (field) => getEmployeePersonalValue(currentPayload, field.path) !== getEmployeePersonalValue(baselinePayload, field.path),
  );
}

const CYRILLIC_LATIN_LOOKALIKE_MAP: Record<string, string> = {
  А: 'A',
  В: 'B',
  Е: 'E',
  К: 'K',
  М: 'M',
  Н: 'H',
  О: 'O',
  Р: 'P',
  С: 'C',
  Т: 'T',
  У: 'Y',
  Х: 'X',
  а: 'A',
  в: 'B',
  е: 'E',
  к: 'K',
  м: 'M',
  н: 'H',
  о: 'O',
  р: 'P',
  с: 'C',
  т: 'T',
  у: 'Y',
  х: 'X',
};

export function normalizeEmployeePersonalLatinDocumentValue(
  value: string,
  options: { allowDigits: boolean },
): string {
  const latinized = value
    .replace(/[АВЕКМНОРСТУХавекмнорстух]/g, (char) => CYRILLIC_LATIN_LOOKALIKE_MAP[char] ?? char)
    .toUpperCase();
  return latinized.replace(options.allowDigits ? /[^A-Z0-9]/g : /[^A-Z]/g, '');
}

export function setEmployeePersonalValue(
  payload: Record<string, unknown>,
  path: string,
  value: string,
): Record<string, unknown> {
  const next = structuredClone(payload || {}) as Record<string, unknown>;
  const parts = path.split('.');
  let cursor: Record<string, unknown> = next;
  for (const part of parts.slice(0, -1)) {
    const current = cursor[part];
    if (!current || typeof current !== 'object' || Array.isArray(current)) {
      cursor[part] = {};
    }
    cursor = cursor[part] as Record<string, unknown>;
  }
  const leaf = parts[parts.length - 1];
  if (path === 'documents.personalNumber') {
    cursor[leaf] = normalizeEmployeePersonalLatinDocumentValue(value, { allowDigits: true });
  } else if (path === 'documents.identityDocument.series') {
    cursor[leaf] = normalizeEmployeePersonalLatinDocumentValue(value, { allowDigits: false });
  } else {
    cursor[leaf] = value;
  }
  return next;
}

export function setEmployeePersonalEmergencyContacts(
  payload: Record<string, unknown>,
  contacts: EmployeePersonalEmergencyContact[],
): Record<string, unknown> {
  const next = structuredClone(payload || {}) as Record<string, unknown>;
  if (!isRecord(next.contacts)) {
    next.contacts = {};
  }
  (next.contacts as Record<string, unknown>).emergencyContacts = contacts.map((contact) => ({
    name: contact.name,
    phone: contact.phone,
    relation: contact.relation,
  }));
  return next;
}
